import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronRight,
  FolderTree,
  Layers,
  Loader2,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";
import { api } from "@/api/client";
import PublicHeader from "@/components/common/PublicHeader";
import DokumenTerkaitView from "@/components/taxonomy/DokumenTerkaitView";
import CitedContent from "@/components/taxonomy/CitedContent";
import { buildSections } from "@/components/taxonomy/aspectSections";
import type { TaxonomyNode } from "@/components/taxonomy/TaxonomyCascadePicker";

const PRIMARY = "#1B5189";
const TAXONOMY_TYPE = "PNBP_DOMAIN";

interface Aspect {
  id: string;
  kode: string;
  nama: string;
  urutan: number;
  is_active: boolean;
  parent_id: string | null;
}

interface NodeContent {
  id: string;
  aspect_kode: string;
  aspect_nama: string;
  title: string | null;
  content_html: string | null;
  status: string;
  urutan: number;
}

// Flatten the nested tree into a Map for O(1) lookup + ancestor walking.
function indexTree(roots: TaxonomyNode[]): Map<string, TaxonomyNode> {
  const map = new Map<string, TaxonomyNode>();
  const walk = (n: TaxonomyNode) => {
    map.set(n.id, n);
    (n.children ?? []).forEach(walk);
  };
  roots.forEach(walk);
  return map;
}

/**
 * Public, read-only taxonomy explorer (no login). Breadcrumb drill-down:
 * click a category card to descend, breadcrumb to climb back. Location is kept
 * in the URL via ?node=<id> so refresh/share preserves position. Shows the
 * focused node's PUBLISHED content + related documents (public preview links).
 */
export default function PublicTaxonomyPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const nodeId = searchParams.get("node");

  const { data: roots = [], isLoading } = useQuery<TaxonomyNode[]>({
    queryKey: ["public-taxonomy-tree", TAXONOMY_TYPE],
    queryFn: () =>
      api.get("/public/taxonomy/tree", { taxonomy_type: TAXONOMY_TYPE }),
  });

  const nodeMap = useMemo(() => indexTree(roots), [roots]);

  // Node in focus. No ?node → the single root (or null if multiple roots, in
  // which case the top-level grid shows the roots themselves).
  const currentNode: TaxonomyNode | null = useMemo(() => {
    if (nodeId) return nodeMap.get(nodeId) ?? null;
    return roots.length === 1 ? roots[0] : null;
  }, [nodeId, nodeMap, roots]);

  // Ancestor chain (root → current) for the breadcrumb.
  const ancestors = useMemo(() => {
    const chain: TaxonomyNode[] = [];
    let cur: TaxonomyNode | null = currentNode;
    while (cur) {
      chain.unshift(cur);
      cur = cur.parent_id ? (nodeMap.get(cur.parent_id) ?? null) : null;
    }
    return chain;
  }, [currentNode, nodeMap]);

  // Cards to drill into: children of the focused node, or the roots at top.
  const cards = useMemo(() => {
    const list = currentNode ? (currentNode.children ?? []) : roots;
    return [...list].sort((a, b) => a.urutan - b.urutan);
  }, [currentNode, roots]);

  const { data: contents = [] } = useQuery<NodeContent[]>({
    queryKey: ["public-taxonomy-contents", currentNode?.id],
    queryFn: () => api.get(`/public/taxonomy/${currentNode!.id}/contents`),
    enabled: !!currentNode?.id,
  });

  // Master aspects → the tab bar (Definisi … Dokumen Terkait).
  const { data: aspects = [] } = useQuery<Aspect[]>({
    queryKey: ["public-taxonomy-aspects"],
    queryFn: () => api.get("/public/taxonomy/aspects"),
  });
  const sortedAspects = useMemo(
    () => [...aspects].sort((a, b) => a.urutan - b.urutan),
    [aspects],
  );

  // Aspek dikelompokkan menjadi section; tab = section.
  const sections = useMemo(() => buildSections(sortedAspects), [sortedAspects]);

  // Active section tab. Compute the effective value (default = first section)
  // rather than syncing through a useEffect — avoids re-render loops.
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const effectiveSectionId = activeSectionId ?? sections[0]?.id ?? null;
  const activeSection =
    sections.find((s) => s.id === effectiveSectionId) ?? null;

  const goTo = (id: string | null) => {
    if (id) setSearchParams({ node: id });
    else setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <PublicHeader />

      {/* ─── Hero + breadcrumb ──────────────────────────────────── */}
      <section className="bg-slate-50 border-b border-slate-200 py-12">
        <div className="mx-auto max-w-5xl px-6">
          <nav className="flex items-center gap-1 mb-6 text-xs text-slate-400 flex-wrap">
            <Link to="/" className="hover:text-slate-600 transition-colors">
              Beranda
            </Link>
            <ChevronRight className="h-3 w-3 flex-shrink-0" />
            <button
              type="button"
              onClick={() => goTo(null)}
              className="hover:text-slate-600 transition-colors"
            >
              Taksonomi
            </button>
            {ancestors.map((a, i) => (
              <span key={a.id} className="flex items-center gap-1">
                <ChevronRight className="h-3 w-3 flex-shrink-0" />
                {i === ancestors.length - 1 ? (
                  <span className="text-slate-700 font-semibold">{a.nama}</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => goTo(a.id)}
                    className="hover:text-slate-600 transition-colors"
                  >
                    {a.nama}
                  </button>
                )}
              </span>
            ))}
          </nav>

          <div className="flex items-start gap-4">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-xl text-white flex-shrink-0"
              style={{ backgroundColor: PRIMARY }}
            >
              <FolderTree className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {currentNode ? currentNode.nama : "Taksonomi PNBP"}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {currentNode?.deskripsi ||
                  "Jelajahi hierarki pengetahuan Penerimaan Negara Bukan Pajak — klik kategori untuk menelusuri lebih dalam."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Content ────────────────────────────────────────────── */}
      <section className="mx-auto max-w-5xl px-6 py-10 space-y-8">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 text-sm text-slate-500 py-16">
            <Loader2 className="h-4 w-4 animate-spin" /> Memuat taksonomi…
          </div>
        ) : (
          <>
            {/* Sub-kategori cards (drill-down) */}
            {cards.length > 0 && (
              <div>
                <h2 className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-3 flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5" /> Sub-Kategori
                </h2>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {cards.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => goTo(c.id)}
                      className="text-left rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md hover:border-blue-300"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-xs text-slate-400">
                          {c.kode || "—"}
                        </span>
                        <ChevronRight className="h-4 w-4 text-slate-300" />
                      </div>
                      <p className="mt-1 font-semibold text-gray-900 break-words">
                        {c.nama}
                      </p>
                      {c.deskripsi && (
                        <p className="mt-1 text-xs text-slate-500 line-clamp-2">
                          {c.deskripsi}
                        </p>
                      )}
                      {(c.children?.length ?? 0) > 0 && (
                        <p className="mt-2 text-xs text-blue-600">
                          {c.children!.length} sub-kategori →
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Section tabs + active panel */}
            {currentNode && sections.length > 0 && (
              <div>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {sections.map((s) => {
                    const active = effectiveSectionId === s.id;
                    // Highlight sections whose aspects actually have content (●),
                    // except Dokumen which is sourced from documents.
                    const filled =
                      !s.isDokumen &&
                      s.aspects.some((a) =>
                        contents.some(
                          (c) =>
                            c.aspect_kode === a.kode &&
                            (c.content_html || c.title),
                        ),
                      );
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setActiveSectionId(s.id)}
                        className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                          active
                            ? "border-blue-600 bg-blue-600 text-white"
                            : filled
                              ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                              : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {s.label}
                        {filled && !active && <span className="ml-1">●</span>}
                      </button>
                    );
                  })}
                </div>

                {activeSection?.isDokumen ? (
                  // Related documents only render when this tab is opened.
                  <DokumenTerkaitView nodeId={currentNode.id} variant="public" />
                ) : activeSection ? (
                  <div className="space-y-4">
                    {activeSection.aspects.map((a) => {
                      const content = contents.find(
                        (c) => c.aspect_kode === a.kode,
                      );
                      return (
                        <article
                          key={a.id}
                          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                        >
                          {activeSection.aspects.length > 1 && (
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                              {a.nama}
                            </h3>
                          )}
                          {content?.title && (
                            <h4 className="text-sm font-semibold text-blue-700 mb-2">
                              {content.title}
                            </h4>
                          )}
                          {content?.content_html ? (
                            <CitedContent
                              variant="public"
                              html={content.content_html}
                            />
                          ) : (
                            <p className="text-sm text-slate-400 italic">
                              Belum ada konten untuk aspek ini.
                            </p>
                          )}
                        </article>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            )}

            {/* Empty state */}
            {!currentNode && cards.length === 0 && (
              <div className="text-center text-sm text-slate-400 py-16">
                Belum ada data taksonomi.
              </div>
            )}
          </>
        )}
      </section>

      {/* ─── Footer ─────────────────────────────────────────────── */}
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-10">
          <div className="flex flex-col items-center justify-between gap-4 text-xs text-slate-500 md:flex-row">
            <div>© 2026 Direktorat Jenderal Anggaran. All rights reserved.</div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" /> dja@kemenkeu.go.id
              </span>
              <span className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" /> Jakarta, Indonesia
              </span>
              <span className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" /> +62-xxxxxxxxxx
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
